import { useTranslations } from '@vocab/react';
import { Notice, Stack, Text } from 'braid-design-system';
import { useEffect, useState } from 'react';

import { ColumnCard } from 'src/components/Card/ColumnCard';
import { LoadingItem } from 'src/components/LoadingBlock/LoadingBlock';
import { VERIFICATIONS } from 'src/components/Router';
import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';
import type { AnalyticsVerificationsWidgetCallToAction } from 'src/modules/analytics/types';

import translations from './.vocab';
import { VerificationsNudge } from './Nudges/VerificationsNudge';
import {
  VerificationsDrawer,
  type VerificationsDrawerTab,
} from './VerificationsDrawer';
import {
  getAnalyticsWidgetName,
  verificationsWidgetCallToActionToLinkText,
} from './analyticsUtils';
import { useVerifications } from './data';

const ExpiryMessage = ({ expiryCount }: { expiryCount: number }) => {
  const { t } = useTranslations(translations);

  return (
    <Notice tone="critical">
      <Text>
        {t('N expired or expiring soon', {
          expiryCount: expiryCount.toString(),
        })}
      </Text>
    </Notice>
  );
};

export const Verifications = () => {
  const { t } = useTranslations(translations);

  const { getState, verificationsWidgetViewed, verificationsWidgetClicked } =
    useAnalytics();

  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [activeDrawerTab, setActiveDrawerTab] =
    useState<VerificationsDrawerTab>('verified');

  const { isLoggedIn } = getState();

  const {
    verifiedCredentialCount,
    verifiableCredentialCount,
    expiryCount,
    nudge,
    loading,
    error,
  } = useVerifications();
  const showLoading = Boolean(loading || error);

  const hasVerifiedVerifications =
    (verifiedCredentialCount && verifiedCredentialCount > 0) || false;

  const hasVerifiableOrExpiryVerifications =
    (verifiableCredentialCount && verifiableCredentialCount > 0) ||
    (expiryCount && expiryCount > 0) ||
    false;

  const openDrawerTab = (drawerTab: VerificationsDrawerTab) => {
    setActiveDrawerTab(drawerTab);
    setShowDrawer(true);
  };

  const verificationCtaAnalyticsOnClick = ({
    callToAction,
  }: {
    callToAction: AnalyticsVerificationsWidgetCallToAction;
  }) => {
    const linkText = verificationsWidgetCallToActionToLinkText[callToAction];
    const widgetName = nudge ? getAnalyticsWidgetName(nudge) : undefined;
    verificationsWidgetClicked({
      callToAction,
      widgetName,
      linkText,
    });
  };

  const cardButtonOnClick = () => {
    verificationCtaAnalyticsOnClick({ callToAction: 'view_verifications' });
    openDrawerTab(
      hasVerifiableOrExpiryVerifications ? 'verifiable' : 'verified',
    );
  };

  const loader = (
    <Stack space="medium">
      <LoadingItem type="text" size="standard" width="medium" />
      <LoadingItem type="text" size="standard" width="large" lines={2} />
      <LoadingItem type="button" />
    </Stack>
  );

  const cardButtonProps = {
    onClick: cardButtonOnClick,
    label: t('See verifications'),
  };

  const unverifiedCardContent = <Text>{t('No verifications')}</Text>;

  const verifiedCardContent = verifiedCredentialCount ? (
    <Text>{t('N verifications', { count: verifiedCredentialCount })}</Text>
  ) : null;

  useEffect(() => {
    if (isLoggedIn && nudge) {
      const widgetName = nudge ? getAnalyticsWidgetName(nudge) : undefined;
      verificationsWidgetViewed({ widgetName });
    }
  }, [isLoggedIn, nudge, verificationsWidgetViewed]);

  return (
    <>
      <ColumnCard
        name={VERIFICATIONS}
        title={t('Verifications')}
        loader={loader}
        loading={showLoading}
        buttonProps={cardButtonProps}
      >
        <Stack space="large">
          {hasVerifiedVerifications
            ? verifiedCardContent
            : unverifiedCardContent}
          {expiryCount && expiryCount > 0 ? (
            <ExpiryMessage expiryCount={expiryCount} />
          ) : null}
          {nudge ? (
            <VerificationsNudge
              nudge={nudge}
              openVerifiableTab={() => openDrawerTab('verifiable')}
              analyticsOnClick={verificationCtaAnalyticsOnClick}
            />
          ) : null}
        </Stack>
      </ColumnCard>
      <VerificationsDrawer
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        activeTab={activeDrawerTab}
        setActiveTabFn={setActiveDrawerTab}
      />
    </>
  );
};
